import type { ReactElement } from 'react';
import React from 'react';
import { useParams } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';
import Loading from 'atom-components/loading';
import LoadingErrorAlert from 'feature-components/loading-error-alert';
import SystemErrorAlert from 'feature-components/system-error-alert';
import MainLayout from 'layouts/main';
import { isResponseReadyToBeMarked } from 'pages/marking/helpers/response.helpers';
import { useMe } from 'services/assist-api/entities/me/me';
import { useTestInstanceResponseMark } from 'services/assist-api/entities/test-instance-response-mark/hooks';
import { type TestInstanceResponseMarkWithItems } from 'services/assist-api/entities/test-instance-response-mark/types';
import { useTestInstanceResponseMarkers } from 'services/assist-api/entities/test-instance-response-marker/hooks';
import { type TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import { useTestInstanceResponse } from 'services/assist-api/entities/test-instance-response/test-instance-response';
import { type TestInstanceResponseWithResponseItemsAndMediaObjects } from 'services/assist-api/entities/test-instance-response/types';
import { useTestInstance } from 'services/assist-api/entities/test-instance/test-instance';
import { useTestRubrics } from 'services/assist-api/entities/test-rubric/hooks';
import { isInitialLoad } from 'utils/check-query-status';
import Breadcrumbs from './breadcrumbs';
import MarkResponseAuthoriser from './mark-response-authoriser';

export default function MarkResponsePage(): ReactElement {
  const { responseId } = useParams(); // Extracted from the slug
  const [queryParams] = useQueryParams({
    markId: StringParam
  });

  const testInstanceResponseMarkersQuery =
    useTestInstanceResponseMarkers<TestInstanceResponseMarkerWithCustomerUser>(
      {
        filters: { testInstanceResponse: responseId },
        groups: ['testinstanceresponsemarker-user'],
        include: 'users'
      },
      { enabled: !!responseId }
    );

  // Get the current user's details from the cache. We should have an earlier check to ensure that
  // the user has joined and is switched to an organisation by this point:
  const meQuery = useMe();

  // Get the saved mark if a mark ID is specified. New marks haven't been saved yet, so this query
  // should not prevent the form from loading.
  const testInstanceResponseMarkQuery =
    useTestInstanceResponseMark<TestInstanceResponseMarkWithItems>(queryParams.markId, {
      groups: [
        'id', // Adds the ID field to all subentities as well
        'testinstanceresponsemark-markitems',
        'testinstanceresponsemarkitem-submittedvalue',
        'testinstanceresponsemarkitem-testrubricitem',
        'testinstanceresponsemarkitem-submittedoptions',
        'testinstanceresponsemarkitem-testinstanceresponsemarker', // The active marker of the mark item
        'testinstanceresponsemarkitem-testinstanceresponseitem',
        'testinstanceresponsemark-tags'
      ],
      include: 'markItems,markers,tags'
    });

  const testInstanceResponseQuery =
    useTestInstanceResponse<TestInstanceResponseWithResponseItemsAndMediaObjects>(responseId, {
      groups: [
        'testinstanceresponse-responseitems',
        'testinstanceresponse-mediaobjects',
        'mediaobject-list'
      ],
      include: 'responseItems,mediaObjects'
    });

  const testInstanceQuery = useTestInstance(testInstanceResponseQuery.data?.json.testInstance.id);

  const testInstanceTestId = testInstanceQuery.data?.json.test.id;
  const testRubricsQuery = useTestRubrics(
    {
      filters: { tests: testInstanceTestId },
      groups: ['testrubric-mediaobjects', 'mediaobject-list'],
      include: 'mediaObjects'
    },
    { enabled: !!testInstanceTestId }
  );

  const isLoading =
    isInitialLoad(
      testInstanceQuery,
      testInstanceResponseQuery,
      testInstanceResponseMarkersQuery,
      testRubricsQuery,
      testInstanceResponseMarkQuery
    ) ||
    !meQuery?.data?.customerUser ||
    !meQuery?.data?.customerRoles;

  const isError =
    testInstanceQuery.isError ||
    testInstanceResponseQuery.isError ||
    testInstanceResponseMarkersQuery.isError ||
    testRubricsQuery.isError ||
    testInstanceResponseMarkQuery.isError;

  /**
   * Currently we are only fetching the rubric items for the first rubric. This is because for CIPS
   * Writing (currently the only test that will use the Marking Response page), there is expected to
   * only ever be only rubric per test.
   * If we need to mark tests with multiple rubrics, this logic will need to be expanded.
   */
  const rubric = testRubricsQuery.data?.json[0];

  const responseReadyToBeMarked = isResponseReadyToBeMarked(
    testInstanceResponseQuery.data?.json.status
  );

  const assessment = testInstanceQuery.data?.json;
  const response = testInstanceResponseQuery.data?.json;
  const currentOrganisationUser = meQuery.data?.customerUser;

  return (
    <MainLayout title="Marking" breadcrumbs={<Breadcrumbs />}>
      {isLoading && <Loading />}
      {!isLoading && isError && (
        <LoadingErrorAlert
          queries={[
            testInstanceQuery,
            testInstanceResponseQuery,
            testInstanceResponseMarkersQuery,
            testRubricsQuery,
            testInstanceResponseMarkQuery
          ]}
        />
      )}
      {!isLoading && !isError && (
        <>
          {!responseReadyToBeMarked && (
            <SystemErrorAlert>
              Response is not closed or finished, so it can't be marked.
            </SystemErrorAlert>
          )}
          {!response && (
            <SystemErrorAlert>
              No response found for the response ID specified in the URL, so it can't be marked.
            </SystemErrorAlert>
          )}
          {!rubric && (
            <SystemErrorAlert>
              No rubric found for this test, so it can't be marked.
            </SystemErrorAlert>
          )}
          {!assessment && (
            <SystemErrorAlert>
              No assessment found for this response, so it can't be marked.
            </SystemErrorAlert>
          )}
          {!currentOrganisationUser && (
            <SystemErrorAlert>
              No organisation user found for the current user, so marking cannot take place.
            </SystemErrorAlert>
          )}
          {rubric &&
            assessment &&
            response &&
            currentOrganisationUser &&
            responseReadyToBeMarked && (
              <MarkResponseAuthoriser
                assessment={assessment}
                response={response}
                responseMarkers={testInstanceResponseMarkersQuery.data?.json || []}
                rubric={rubric}
                currentOrganisationUser={currentOrganisationUser}
                mark={testInstanceResponseMarkQuery.data?.json}
              />
            )}
        </>
      )}
    </MainLayout>
  );
}
